// Pure data-shaping for the BHI assessment-kiosk resident picker. CommonJS so
// Jest can require it directly (the repo has no ESM transform); esbuild bundles
// it for the admin page via `import { … }`. Reuses isBhiEnrolledPatient from
// workflow.js (CommonJS) so the BHI-enrolled predicate stays single-source.

const { isBhiEnrolledPatient } = require('./workflow');

function displayName(data) {
  return [data && data.fname, data && data.lname].filter(Boolean).join(' ').trim() || '(unnamed)';
}

function normTags(data) {
  return Array.isArray(data && data.tags) ? data.tags.filter(Boolean).map(String) : [];
}

// patients: rows from listPatients() — note GET /data/patients returns FLAT
// patient objects ({ id, fname, billing, tags, ... }), so the blob IS the row;
// a wrapped { id, data } shape is also tolerated (matches studio.jsx adaptPatient).
// rosteredIds: array or Set of patientIds already on the facility.
// → BHI-enrolled, not-yet-rostered candidates.
function bhiEnrolledCandidates(patients, rosteredIds) {
  const excluded = rosteredIds instanceof Set ? rosteredIds : new Set(rosteredIds || []);
  const out = [];
  for (const p of (patients || [])) {
    if (!p || !p.id || excluded.has(p.id)) continue;
    const data = (p.data && typeof p.data === 'object') ? p.data : p;
    if (!isBhiEnrolledPatient(data)) continue;
    out.push({ patientId: p.id, displayName: displayName(data), tags: normTags(data) });
  }
  return out;
}

// Unique tags across candidates, case-insensitively sorted, with counts.
function collectTags(candidates) {
  const byLower = new Map(); // lower -> first-seen display form
  const counts = new Map();  // display form -> count
  for (const c of (candidates || [])) {
    for (const t of (c.tags || [])) {
      const lo = String(t).toLowerCase();
      if (!byLower.has(lo)) byLower.set(lo, t);
      const disp = byLower.get(lo);
      counts.set(disp, (counts.get(disp) || 0) + 1);
    }
  }
  return [...byLower.values()]
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((tag) => ({ tag, count: counts.get(tag) || 0 }));
}

// The candidate's alphabetically-first tag, or '' when untagged.
function primaryTag(c) {
  const ts = ((c && c.tags) || []).slice().sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  return ts[0] || '';
}

// Narrow by tag (null/'' = all) + query (name or id substring, case-insensitive),
// then sort grouped by primary tag (untagged last), then by displayName.
function filterSortCandidates(candidates, opts) {
  const { tag = null, query = '' } = opts || {};
  const q = String(query).trim().toLowerCase();
  const tagLo = tag ? String(tag).toLowerCase() : null;
  const filtered = (candidates || []).filter((c) => {
    if (tagLo && !(c.tags || []).some((t) => String(t).toLowerCase() === tagLo)) return false;
    if (q && !(`${c.displayName} ${c.patientId}`.toLowerCase().includes(q))) return false;
    return true;
  });
  return filtered.slice().sort((a, b) => {
    // Group by primary tag case-insensitively (so 'oak' and 'Oak' are one group,
    // consistent with collectTags/primaryTag); untagged ('') sorts last.
    const pa = primaryTag(a).toLowerCase();
    const pb = primaryTag(b).toLowerCase();
    if (pa !== pb) {
      if (!pa) return 1;          // untagged sorts last
      if (!pb) return -1;
      return pa.localeCompare(pb);
    }
    return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
  });
}

module.exports = { bhiEnrolledCandidates, collectTags, primaryTag, filterSortCandidates };
